import { converters as tableConverters } from 'store/table';
import { converters as filesConverters } from 'store/files';
import {
  IApiIntegrationLogEvent,
  IIntegration,
  IIntegrationLog,
  IIntegrationLogItem,
  IIntegrationModel,
} from 'types/income';
import { loaders } from './integrations.loaders';
import {
  ApiIntegrationLogEvent,
  Integration,
  IntegrationLog,
  IntegrationLogItem,
  IntegrationModel,
} from './integrations.types';

const convertIntegrationModel = (
  model: IIntegrationModel,
): IntegrationModel => {
  return {
    id: model.id,
    name: model.name,
    status: model.status,
    variant: model.type,
    logo: model.logo ? filesConverters.getUploadedFile(model.logo) : null,
    comment: model.comment ?? '',
    createdAt: model.creationDate,
  };
};

const convertIntegrationModels = (
  models: IIntegrationModel[],
): IntegrationModel[] => {
  return models.map(convertIntegrationModel);
};

const convertIntegration = (integration: IIntegration): Integration => {
  return {
    id: integration.id,
    name: integration.name,
    status: integration.status,
    parentCompanyId: integration.parentCompanyId,
    model: convertIntegrationModel(integration.integrationType),
    admin: integration.admin,
  };
};

const convertIntegrations = (integrations: IIntegration[]): Integration[] => {
  return integrations.map(convertIntegration);
};

const convertIntegrationLogItem = (
  logItem: IIntegrationLogItem,
): IntegrationLogItem => {
  return {
    id: logItem.id,
    processId: logItem.processId,
    level: logItem.level,
    creationDate: logItem.creationDate,
    msg: logItem.msg,
  };
};

const convertIntegrationLogItems = (
  logItems: IIntegrationLogItem[],
): IntegrationLogItem[] => {
  return logItems.map(convertIntegrationLogItem);
};

const convertIntegrationLog = (log: IIntegrationLog): IntegrationLog => {
  return {
    clientIntegrationTypeId: log.clientIntegrationTypeId,
    processId: log.processId,
    startDate: log.startDate,
    endDate: log.endDate,
    status: log.status,
    hasWarningLogItems: log.hasWarningLogItems,
    hasErrorLogItems: log.hasErrorLogItems,
  };
};

const convertIntegrationLogs = (logs: IIntegrationLog[]): IntegrationLog[] => {
  return logs.map(convertIntegrationLog);
};

const convertIntegrationLogEvent = (
  event: IApiIntegrationLogEvent,
): ApiIntegrationLogEvent => {
  return {
    timestamp: event.timestamp,
    createAt: new Date(event.timestamp).toISOString(),
    level: event.level,
    method: event.method,
    url: event.url,
    request: `${event.method}: ${event.url}`,
    raw: event,
  };
};

const convertIntegrationLogEvents = (
  events: IApiIntegrationLogEvent[],
): ApiIntegrationLogEvent[] => {
  return events.map(convertIntegrationLogEvent);
};

export const converters = {
  getIntegrations: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getIntegrations>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertIntegrations(data.data),
      total,
      page,
      pageSize,
      pages,
    };
  },
  getIntegration: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getIntegration>>) => {
    return convertIntegration(data);
  },
  getIntegrationModels: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getIntegrationModels>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertIntegrationModels(data.data),
      total,
      page,
      pageSize,
      pages,
    };
  },
  getIntegrationModel: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getIntegrationModel>>) => {
    return convertIntegrationModel(data);
  },
  getIntegrationLogs: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getIntegrationLogs>>) => {
    return convertIntegrationLogs(data.data);
  },
  getIntegrationLogItems: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getIntegrationLogItems>>) => {
    return convertIntegrationLogItems(data.data);
  },
  getIntegrationLogEvents: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getIntegrationLogEvents>>) => {
    return {
      events: convertIntegrationLogEvents(data.events),
      nextToken: data.nextToken ?? null,
    };
  },
};
